import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

interface SocialAuthProps {
  isLoading: boolean;
  onSocialAuth: (provider: 'email' | 'google' | 'apple') => void;
}

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const AppleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 384 512">
    <path
      fill="currentColor"
      d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
    />
  </svg>
);

const SocialAuth: React.FC<SocialAuthProps> = ({ isLoading, onSocialAuth }) => {
  const navigate = useNavigate();
  const socialProviders = [
    {
      id: 'email' as const,
      name: 'Email',
      icon: 'Mail' as const,
      bgColor: 'bg-white hover:bg-gray-50',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-300',
    },
    {
      id: 'google' as const,
      name: 'Google',
      customIcon: <GoogleIcon />,
      bgColor: 'bg-white hover:bg-gray-50',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-300',
    },
    {
      id: 'apple' as const,
      name: 'Apple',
      customIcon: <AppleIcon />,
      bgColor: 'bg-white hover:bg-gray-50',
      textColor: 'text-black',
      borderColor: 'border-gray-300',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-gray-500 font-medium">Or continue with</span>
        </div>
      </div>

      {/* Social Buttons */}
      <div className="grid grid-cols-3 gap-3">
        {socialProviders.map((provider) => (
          <button
            key={provider.id}
            type="button"
            onClick={() => onSocialAuth(provider.id)}
            disabled={isLoading}
            className={`
              flex items-center justify-center p-3 rounded-xl border-2 transition-all
              ${provider.bgColor} ${provider.textColor} ${provider.borderColor}
              hover:shadow-md active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed
            `}
            title={`Sign in with ${provider.name}`}
          >
            {provider.customIcon ? provider.customIcon : <Icon name={provider.icon!} size={20} />}
          </button>
        ))}
      </div>

      {/* Security Notice */}
      <p className="text-xs text-center text-gray-500 mt-4">
        By continuing, you agree to NOONX's{' '}
        <button type="button" onClick={() => navigate('/terms-of-service')} className="text-[#E42313] hover:underline font-medium">
          Terms of Service
        </button>{' '}
        and{' '}
        <button type="button" onClick={() => navigate('/privacy-policy')} className="text-[#E42313] hover:underline font-medium">
          Privacy Policy
        </button>
      </p>
    </div>
  );
};

export default SocialAuth;